import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import PageLayout from "../../Layouts/PageLayout/PageLayout";
import TabsNavigation from "../../components/common/TabsNavigation/TabsNavigation";
import Button from "../../components/common/Button/Button";
import { FiDownload } from "react-icons/fi";
import CalendarioTab from "../../components/layout/Gestores/CalendarioTab/CalendarioTab";
import { getApiInstance } from "../../utils/api";
import { CiCircleCheck, CiCircleQuestion, CiClock1 } from "react-icons/ci";
import { CgDanger } from "react-icons/cg";
import Table from "../../components/common/Table/Table";
import MapComponent from "../../components/common/MapComponent/MapComponent";
import Modal from "../../components/common/Modal/Modal";
import Badge from "../../components/common/Badge/Badge";
import { useZone } from "../../context/ZoneContext";
import "./Gestores.scss";
import { mapList } from "../../utils/utils";
import { FaCheckCircle, FaClock, FaQuestionCircle } from "react-icons/fa";
import { Link } from "react-router-dom";
import { IoIosAlert, IoIosCloseCircle } from "react-icons/io";

const columnasGestores = [
    { key: "codProveedor", label: "ID", visible: false },
    {
        key: "nombre",
        label: "Gestor",
        visible: true,
        sortable: true,
        render: (row) => {
            const href = `/gestores/${row.codProveedor}`;

            let ubicacion = "";
            if (row.localidad && row.pais) {
                ubicacion = `${row.localidad}, ${row.pais}`;
            } else if (row.localidad) {
                ubicacion = row.localidad;
            } else if (row.pais) {
                ubicacion = row.pais;
            }

            return (
                <Link to={href}>
                    <div className="table-cell-content">
                        <span className="table-cell-content__primary table-cell-content__primary--bold">
                            {row.nombre}
                        </span>
                        {ubicacion && (
                            <span className="table-cell-content__secondary">
                                {ubicacion}
                            </span>
                        )}
                    </div>
                </Link>
            );
        },
    },
    {
        key: "cif",
        label: "CIF",
        visible: true,
        sortable: true,
        visibleOnExpand: true,
    },
    { key: "localidad", label: "Localidad", visible: false, sortable: false },
    {
        key: "contacto",
        label: "Contacto",
        visible: true,
        sortable: true,
        width: "18%",
        visibleOnExpand: true,
        render: (row) => {
            return (
                <div className="table-cell-content">
                    <span className="table-cell-content__primary">
                        {row.contacto || "-"}
                    </span>
                    <span className="table-cell-content__secondary">
                        {row.eMail || ""}
                    </span>
                </div>
            );
        },
    },
    {
        key: "telefono",
        label: "Teléfono",
        visible: true,
        sortable: true,
        visibleOnExpand: true,
    },
    { key: "eMail", label: "Email", visible: false, sortable: false },
    {
        key: "codEstado",
        label: "Estado",
        visible: true,
        sortable: true,
        visibleOnExpand: true,
    },
];

export default function Gestores({ user }) {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState("listado");
    const [gestores, setGestores] = useState([]);
    const [certificados, setCertificados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { selectedZone } = useZone();
    const [comunidades, setComunidades] = useState([]);
    const [paises, setPaises] = useState([]);
    const [provincias, setProvincias] = useState([]);
    const [residuos, setResiduos] = useState([]);
    const [selectedProveedores, setSelectedProveedores] = useState(new Set());

    const selectedItems = useMemo(
        () =>
            gestores.filter((proveedor) =>
                selectedProveedores.has(proveedor.codProveedor)
            ),
        [gestores, selectedProveedores]
    );

    const fetchGestores = async () => {
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/Proveedores/GetProveedoresByZona?CodZona=${selectedZone.value}`
            );
            setGestores(response.data);
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar los gestores. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchCertificados = async () => {
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/ProveedoresCentros/GetAllCertificados/${selectedZone.value}`
            );
            setCertificados(response.data);
        } catch (err) {
            console.error("Error al cargar los certificados:", err);
        }
    };

    const filterFields = [
        { key: "nombre", label: t("fields.name"), type: "multiselect" },
        { key: "cif", label: t("fields.cif"), type: "multiselect" },
        { key: "localidad", label: t("fields.locality"), type: "multiselect" },
        { key: "contacto", label: t("fields.contact"), type: "multiselect" },
        { key: "telefono", label: t("fields.phone"), type: "multiselect" },
        { key: "email", label: t("fields.email"), type: "multiselect" },
    ];

    const fetchComunidades = async () => {
        const api = getApiInstance();

        try {
            let comunidadesData = [];

            const response = await api.get(
                `/api/Comunidades/GetComunidadesByZona/${selectedZone.value}`
            );
            comunidadesData = response.data;

            setComunidades([
                ...mapList(
                    comunidadesData,
                    "comunidad",
                    "codComunidad",
                    "codComunidad",
                    "codPais"
                ),
            ]);

            console.log("Comunidades data fetched:", comunidadesData);

            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las comunidades. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchPaises = async () => {
        const api = getApiInstance();

        try {
            let paisesData = [];

            const response = await api.get(
                `/api/Paises/GetPaisesByZona/${selectedZone.value}`
            );
            paisesData = response.data;

            setPaises(mapList(paisesData, "pais", "codPais", "codPais"));

            console.log("Paises data fetched:", paisesData);

            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar los países. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchProvincias = async () => {
        const api = getApiInstance();
        try {
            let provinciasData = [];

            const response = await api.get(
                `/api/Provincias/GetProvinciasByZona/${selectedZone.value}`
            );
            provinciasData = response.data;

            setProvincias([
                ...mapList(
                    provinciasData,
                    "provincia",
                    "codProvincia",
                    "codProvincia",
                    "codComunidad"
                ),
            ]);
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchResiduos = async () => {
        const api = getApiInstance();
        try {
            let residuosData = [];

            const response = await api.get(
                `/api/Residuos/GetResiduosByZona/?codZona=${selectedZone.value}`
            );
            residuosData = response.data;

            setResiduos([
                ...mapList(residuosData, "nombre", "codResiduo", "codResiduo"),
            ]);
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError(null);

        const loadAll = async () => {
            try {
                await fetchGestores();

                await Promise.all([
                    fetchCertificados(),
                    fetchPaises(),
                    fetchComunidades(),
                    fetchProvincias(),
                    fetchResiduos(),
                ]);
            } catch (err) {
                if (isMounted) {
                    setError(
                        "No se han podido cargar los datos. Vuelve a intentarlo más tarde."
                    );
                }
                console.error("Error en loadAll:", err);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        loadAll();

        return () => {
            isMounted = false;
        };
    }, [selectedZone]);

    const tabs = [
        { id: "listado", label: "Listado" },
        { id: "mapa", label: "Mapa" },
        { id: "calendario", label: "Calendario" },
    ];

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
    };

    const modalMap = (gestor) => {
        const status = getComplianceStatus(gestor);
        return (
            <>
                <div className="gestor-map-details__info">
                    <div className="info-row">
                        <strong>{t("fields.name")}:</strong> {gestor.nombre}
                    </div>
                    <div className="info-row">
                        <strong>{t("fields.contact")}:</strong>{" "}
                        {gestor.contacto || t("common.noResults")}
                    </div>
                    <div className="info-row">
                        <strong>{t("fields.phone")}:</strong>{" "}
                        {gestor.telefono || t("common.noResults")}
                    </div>
                    <div className="info-row">
                        <strong>{t("fields.email")}:</strong>{" "}
                        {gestor.eMail || t("common.noResults")}
                    </div>
                    <div className="info-row">
                        <strong>Autorización:</strong> {gestor.cif}
                    </div>
                </div>
                <div className="gestor-map-details__compliance">
                    <div className="compliance-header">
                        <h4 className="compliance-title">
                            {t("sections.legalCompliance")}
                        </h4>
                        <Badge variant={status.type} Icon={status.Icon}>
                            {status.label}
                        </Badge>
                    </div>
                    <div className="gestor-map-details">
                        <h3 className="modal-title">{gestor.nombre}</h3>
                        <p className="gestor-location">{gestor.localidad}</p>

                        <div className="gestor-map-details__info">
                            <div className="info-row">
                                <strong>Gestor:</strong> {gestor.nombre}
                            </div>
                            <div className="info-row">
                                <strong>Contacto:</strong>{" "}
                                {gestor.contacto || "No especificado"}
                            </div>
                            <div className="info-row">
                                <strong>Email:</strong>{" "}
                                {gestor.eMail || "No especificado"}
                            </div>
                            <div className="info-row">
                                <strong>Teléfono:</strong>{" "}
                                {gestor.telefono || "No especificado"}
                            </div>
                            <div className="info-row">
                                <strong>Autorización:</strong> {gestor.cif}
                            </div>
                        </div>
                        <div className="modal-actions">
                            <Button
                                variant="filled"
                                onClick={() => {
                                    window.location.href = `/gestores/${gestor.codProveedor}`;
                                }}
                            >
                                {t("common.view")} Ficha Completa
                            </Button>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    const getComplianceStatus = (gestor) => {
        switch (gestor.codEstado) {
            case 0:
                return { label: "Sin datos", type: "info", Icon: CiClock1 };
            case 1:
                return {
                    label: "Pendiente",
                    type: "info",
                    Icon: CiClock1,
                };
            case 2:
                return {
                    label: "Incidencia",
                    type: "warning",
                    Icon: CgDanger,
                };
            case 3:
                return {
                    label: "Validada",
                    type: "success",
                    Icon: CiCircleCheck,
                };
            case 4:
                return {
                    label: "Expirado",
                    type: "danger",
                    Icon: CiCircleCheck,
                };
            default:
                return { label: "Sin estado", type: "outlined" };
        }
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case "listado":
                return (
                    <Table
                        columns={columnasGestores}
                        dataTable={gestores}
                        keyProp="codProveedor"
                        sortProp="nombre"
                        showCheckboxes={true}
                        expandableBreakpoint={600}
                        expandable={true}
                        noResultsMessage={t("managers.noManagersFound")}
                        actions={[t("common.delete"), t("common.edit")]}
                        searchTerms={[
                            "nombre",
                            "cif",
                            "localidad",
                            "contacto",
                            "telefono",
                            "eMail",
                        ]}
                        filterFields={filterFields}
                        elementName={t("managers.title")}
                        stateProp="codEstado"
                        states={[
                            {
                                label: t("states.noData"),
                                type: "info",
                                Icon: CiClock1,
                            },
                            {
                                label: t("states.pending"),
                                type: "info",
                                Icon: CiClock1,
                            },
                            {
                                label: t("states.incident"),
                                type: "warning",
                                Icon: CgDanger,
                            },
                            {
                                label: t("states.validated"),
                                type: "success",
                                Icon: CiCircleCheck,
                            },
                        ]}
                        formElements={[
                            {
                                title: t("sections.generalInfo"),
                                titleVisible: true,
                                elements: [
                                    {
                                        name: "nombre",
                                        type: "text",
                                        label: t("fields.name"),
                                        placeholder: t(
                                            "placeholders.enterName"
                                        ),
                                        required: true,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                    },
                                    {
                                        name: "cif",
                                        type: "text",
                                        label: t("fields.cif"),
                                        placeholder: t("placeholders.enterCif"),
                                        required: true,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                    },
                                    {
                                        name: "contacto",
                                        type: "text",
                                        label: t("fields.contact"),
                                        placeholder: t(
                                            "placeholders.enterName"
                                        ),
                                        required: false,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                    },
                                    {
                                        name: "telefono",
                                        type: "tel",
                                        label: t("fields.phone"),
                                        placeholder: t(
                                            "placeholders.enterPhone"
                                        ),
                                        required: false,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                    },
                                    {
                                        name: "eMail",
                                        type: "email",
                                        label: t("fields.email"),
                                        placeholder: t(
                                            "placeholders.enterEmail"
                                        ),
                                        required: false,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                    },
                                    {
                                        name: "codsResiduos",
                                        type: "multiselect",
                                        options: residuos,
                                        label: t("managers.form.wasteTypes"),
                                        placeholder: t(
                                            "managers.form.wasteTypesPlaceholder"
                                        ),
                                        required: false,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                        withCheckbox: true,
                                    },
                                    {
                                        name: "isGestorFinal",
                                        type: "checkbox",
                                        label: t("managers.form.finalManager"),
                                        required: false,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                        defaultValue: false,
                                    },
                                    {
                                        name: "isTransportista",
                                        type: "checkbox",
                                        label: t("managers.form.carrier"),
                                        required: false,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                        defaultValue: false,
                                    },
                                ],
                            },
                            {
                                title: t("sections.location"),
                                titleVisible: true,
                                elements: [
                                    {
                                        name: "direccion",
                                        type: "text",
                                        label: t("fields.address"),
                                        placeholder: t(
                                            "placeholders.enterAddress"
                                        ),
                                        required: true,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                    },
                                    {
                                        name: "localidad",
                                        type: "text",
                                        label: t("fields.locality"),
                                        placeholder: t(
                                            "placeholders.enterLocality"
                                        ),
                                        required: true,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                    },
                                    {
                                        name: "cp",
                                        type: "text",
                                        label: t("fields.postalCode"),
                                        placeholder: t(
                                            "placeholders.enterPostalCode"
                                        ),
                                        required: false,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                    },
                                    {
                                        name: "codPais",
                                        type: "select",
                                        label: t("fields.country"),
                                        placeholder: t(
                                            "placeholders.selectCountry"
                                        ),
                                        options: paises,
                                        required: true,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                    },
                                    {
                                        name: "codComunidad",
                                        type: "select",
                                        label: t("fields.community"),
                                        placeholder: t(
                                            "placeholders.selectCommunity"
                                        ),
                                        options: comunidades,
                                        required: true,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                        associatedFormElement: "codPais",
                                    },
                                    {
                                        name: "codProvincia",
                                        type: "select",
                                        label: t("fields.province"),
                                        placeholder: t(
                                            "placeholders.selectProvince"
                                        ),
                                        options: provincias,
                                        required: true,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                        associatedFormElement: "codComunidad",
                                    },
                                    {
                                        name: "codZona",
                                        type: "number",
                                        label: "Zona",
                                        visible: false,
                                        defaultValue: selectedZone.value,
                                    },
                                ],
                            },
                        ]}
                        loading={loading}
                        addApiUrl="/api/Proveedores/SetProveedor"
                        editApiUrl="/api/Proveedores/EditProveedor"
                        deleteApiUrl="/api/Proveedores/DeleteProveedor"
                        selectedRows={selectedProveedores}
                        onSelectionChange={setSelectedProveedores}
                        userCodAcceso={Number(user.codAcceso)}
                        userCodPerfil={Number(user.role)}
                    />
                );
            case "mapa":
                return (
                    <MapComponent
                        data={gestores}
                        center={[42.0, 2.0]}
                        zoom={5}
                        height="600px"
                        width="100%"
                        modalContent={modalMap}
                    />
                );
            case "calendario":
                return <CalendarioTab gestoresData={certificados} />;
            default:
                return null;
        }
    };

    const handleExportarSeleccionados = () => {
        if (selectedItems.length === 0) {
            alert("Por favor, selecciona al menos un proveedor para exportar.");
            return;
        }

        const codsProveedores = selectedItems.map((item) => item.codProveedor);

        generateExcel({ codsProveedores });
    };

    const generateExcel = async ({ codsProveedores }) => {
        const api = getApiInstance();
        const codZona = selectedZone.value;
        const codZonaLegal = 1;

        try {
            const response = await api.post(
                "/api/Proveedores/CreateReportByCods",
                codsProveedores,
                {
                    params: { codZona, codZonaLegal },
                    responseType: "blob",
                }
            );

            const contentDisposition = response.headers["content-disposition"];
            let fileName = "Informe_Proveedores.xlsx";
            if (contentDisposition) {
                const fileNameMatch =
                    contentDisposition.match(/filename="?(.+)"?/);
                if (fileNameMatch && fileNameMatch.length === 2) {
                    fileName = fileNameMatch[1];
                }
            }

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(
                "No se pudo generar el reporte. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        }
    };

    return (
        <PageLayout
            title={t("managers.title")}
            subtitle={t("managers.subtitle")}
            actions={[
                <Button
                    key="export"
                    componentType="button"
                    variant="outlined"
                    startIcon={<FiDownload />}
                    onClick={handleExportarSeleccionados}
                    disabled={selectedItems.length === 0}
                >
                    {t("managers.exportManagers")}
                </Button>,
            ]}
        >
            <TabsNavigation
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={handleTabChange}
            />
            {error && (
                <div
                    style={{
                        padding: "20px",
                        textAlign: "center",
                        fontSize: "16px",
                        color: "#e74c3c",
                        backgroundColor: "#fdf2f2",
                        border: "1px solid #fecaca",
                        borderRadius: "6px",
                        margin: "10px 0",
                    }}
                >
                    {error}
                </div>
            )}

            {!error && renderTabContent()}
        </PageLayout>
    );
}
